import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { 
  Box, 
  Typography, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import { 
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { API_URL, createAxiosInstance } from '../../../api';

const MyPaymentsTab = React.memo(() => {
  const theme = useTheme();
  const userId = useSelector((state) => state.main.userId);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch course/advert payments
  useEffect(() => {
    const fetchPayments = async () => {
      if (!userId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.get(`${API_URL}/api/billings/historyAdverts/${userId}`);
        
        setPayments(response.data.history || []);
        
      } catch (error) {
        console.error('Error fetching payments:', error);
        setError('Failed to load payment history. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, [userId]);

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        height: '400px',
        gap: 2
      }}>
        <CircularProgress size={40} />
        <Typography variant="body1" color="text.secondary">
          Loading payment history...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Payment History ({payments.length})
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Track your course and class advertisement payments
        </Typography>
      </Box>

      {/* Payments List */}
      {payments.length > 0 ? (
        <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: theme.palette.primary.main + '08' }}>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Course Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((payment, index) => (
                <TableRow 
                  key={payment.paymentId || index}
                  sx={{ 
                    '&:hover': { 
                      backgroundColor: theme.palette.primary.main + '04' 
                    } 
                  }}
                >
                  <TableCell>
                    {payment.paymentDate 
                      ? new Date(payment.paymentDate).toLocaleDateString('en-UK', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })
                      : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {payment.courseName || 'No matching course'}
                  </TableCell>
                  <TableCell>
                    {payment.description 
                      ? payment.description.replace(/Subscription/g, 'Advertisement')
                      : 'No description'}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    £{payment.amountPaid || '0.00'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center',
          height: '300px',
          gap: 2
        }}>
          <ReceiptIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
          <Typography variant="h6" color="text.secondary">
            No Payment History
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Your course payment history will appear here once you create and pay for your first course advertisement.
          </Typography>
        </Box>
      )}
    </Box>
  );
});

MyPaymentsTab.displayName = 'MyPaymentsTab';

export default MyPaymentsTab;
